export const similarBooksRules = {
  // Input limits
  maxBooks: 5,
  minBooks: 1,
  
  // UI text
  placeholders: {
    searchInput: "Search for books you've enjoyed (press Enter or click search)",
    emptyState: 'Click the search button or press Enter to find books...'
  },

  // Validation messages
  errors: {
    tooManyBooks: 'Please select up to 5 books',
    tooFewBooks: 'Please select at least 1 book',
    invalidInput: 'Please provide valid book information',
    searchError: 'Unable to search for books. Please try again.'
  },

  // Success messages
  success: {
    bookAdded: 'Book added to your selection',
    bookRemoved: 'Book removed from your selection'
  },

  // Loading states
  loadingStates: {
    searching: 'Searching for books...',
    gettingRecommendations: 'Finding similar books...'
  },
  
  // Search behavior configuration
  searchBehavior: {
    debounceTime: 2000, // Increased to 2 seconds for fallback auto-search (reduces API calls)
    minQueryLength: 3, // Only search after at least 3 characters
    keepFocusOnSearch: true, // Flag to maintain focus during search
    showResultsDelay: 100, // Small delay before showing results to prevent UI jumpiness
    useSearchButton: true // New flag to indicate search button is primary method
  }
};

// Add type checking to our validation
export const validateBookSelection = (selectedBooks, type = 'similar') => {
  // Skip validation for mood-based flow
  if (type === 'mood') {
    return { isValid: true };
  }

  // Only apply these validations for similar books flow
  if (type === 'similar') {
    if (!Array.isArray(selectedBooks)) {
      return { isValid: false, error: 'Invalid selection format' };
    }

    if (selectedBooks.length === 0) {
      return { isValid: false, error: similarBooksRules.errors.tooFewBooks };
    }

    if (selectedBooks.length > similarBooksRules.maxBooks) {
      return { isValid: false, error: similarBooksRules.errors.tooManyBooks };
    }

    // Check if each book has required fields
    const hasInvalidBooks = selectedBooks.some(book => !book.title || !book.author);
    if (hasInvalidBooks) {
      return { isValid: false, error: similarBooksRules.errors.invalidInput };
    }
  }

  return { isValid: true };
};

// Format book data consistently for both flows
export const formatBookData = (book, type = 'similar') => {
  const baseFormat = {
    title: book.title,
    author: book.author,
    thumbnail: book.thumbnail || null,
    id: book.id || `${book.title}-${book.author}`.toLowerCase().replace(/\s+/g, '-')
  };

  // Add any mood-specific fields if needed
  if (type === 'mood') {
    return {
      ...baseFormat,
      mood: book.mood
    };
  }

  return baseFormat;
};

// Helper function to maintain input focus during search operations
export const maintainInputFocus = (inputRef) => {
  if (similarBooksRules.searchBehavior.keepFocusOnSearch && inputRef && inputRef.current) {
    // Use requestAnimationFrame for more reliable focus maintenance across browser rendering cycles
    requestAnimationFrame(() => {
      // Store current selection/cursor position
      const selectionStart = inputRef.current.selectionStart;
      const selectionEnd = inputRef.current.selectionEnd;
      
      // Focus the input
      inputRef.current.focus();
      
      // Restore cursor position/selection if applicable
      if (selectionStart !== undefined && selectionEnd !== undefined) {
        inputRef.current.setSelectionRange(selectionStart, selectionEnd);
      }
    });
  }
};
